import {
    Stack,
    TextField,
    Button,
    Typography,
    Box,
    MenuItem,
} from "@mui/material";

import { useCheckout } from "../CheckoutContext";
import CreditCardPreview from "./CreditCardPreview";
import { getInstallmentLabels } from "../../utils/getInstallmentLabels";


import {
    onlyNumbers,
    getCardBrand,
    getCardLength,
    isValidCardNumber,
    isValidMonth,
    isValidYear,
    isValidCVV,
} from "../../utils/cardValidation";

import { gerarCardToken } from "../../services/pagbankTokenize";

import { useState } from "react";

export default function CreditCardStep() {
    const { checkoutData, items, updateSection, prevStep, nextStep, setActiveStep } = useCheckout();
    const card = checkoutData.payment.card || {};
    const installments = checkoutData.payment.installments || 1;
    const methodFlag = checkoutData.payment.method || "credit"

    const [showBack, setShowBack] = useState(false);

    const handleBack = () => {
        // Limpa o método de pagamento para voltar ao PaymentStep
        updateSection("payment", {
            method: "",
            installments: 1,
            card: null
        });
        // Mantém no step 3, que agora mostrará PaymentStep
        setActiveStep(3);
    };

    const brand = getCardBrand(card.number);
    const maxLength = getCardLength(brand);

    function updateCard(data) {
        updateSection("payment", {
            card: { ...card, ...data },
        });
    }

    function updateInstallments(value) {
        updateSection("payment", {
            installments: value,
            method: "credit",
        });
    }

    const isValid =
        card.number?.length === maxLength &&
        isValidCardNumber(card.number) &&
        card.name?.length >= 5 &&
        isValidMonth(card.month) &&
        isValidYear(card.year) &&
        isValidCVV(card.cvv);

    async function handleConfirm() {

        updateSection("payment", {
          method: methodFlag,
          installments,
        });

        nextStep();

    }

    const subtotal = items
      ? items.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        )
      : 0;

    const shipping = checkoutData.shipping?.price || 0;

    const installmentOptions = getInstallmentLabels({
      subtotal,
      shipping,
    });

    return (
        <Stack spacing={4}>
            <Typography variant="h6">Dados do cartão</Typography>

            {/* PREVIEW */}
            <Box display="flex" justifyContent="center">
                <div className={showBack ? "force-flip" : ""}>
                    <CreditCardPreview
                        number={card.number}
                        name={card.name}
                        month={card.month}
                        year={card.year}
                        cvv={card.cvv}
                    />
                </div>
            </Box>

            {/* FORMULÁRIO */}
            <Stack spacing={2}>
                <TextField
                    label="Número do cartão"
                    value={card.number || ""}
                    onChange={(e) =>
                        updateCard({
                            number: onlyNumbers(e.target.value).slice(0, maxLength),
                        })
                    }
                    sx={{
                        "& .MuiInputLabel-root": {
                            color: "#6b7280",
                            fontSize: 14,
                        },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                        },
                    }}
                />

                <TextField
                    label="Nome no cartão"
                    value={card.name || ""}
                    onChange={(e) =>
                        updateCard({ name: e.target.value.toUpperCase() })
                    }
                    sx={{
                        "& .MuiInputLabel-root": {
                            color: "#6b7280",
                            fontSize: 14,
                        },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                        },
                    }}

                />

                {/* 🔥 LINHA ÚNICA — MM / AA / CVV (MESMO TAMANHO) */}
                <Stack direction="row" spacing={2} width="100%">
                    <TextField
                        label="MM"
                        value={card.month || ""}
                        onChange={(e) =>
                            updateCard({
                                month: onlyNumbers(e.target.value).slice(0, 2),
                            })
                        }
                        onBlur={() => setShowBack(false)}
                        error={card.month && !isValidMonth(card.month)}
                        fullWidth
                        sx={{
                            "& .MuiInputLabel-root": {
                                color: "#6b7280",
                                fontSize: 14,
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}

                    />

                    <TextField
                        label="AA"
                        value={card.year || ""}
                        onChange={(e) =>
                            updateCard({
                                year: onlyNumbers(e.target.value).slice(0, 2),
                            })
                        }
                        onBlur={() => setShowBack(false)}
                        error={card.year && !isValidYear(card.year)}
                        fullWidth
                        sx={{
                            "& .MuiInputLabel-root": {
                                color: "#6b7280",
                                fontSize: 14,
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}

                    />

                    <TextField
                        label="CVV"
                        value={card.cvv || ""}
                        onFocus={() => setShowBack(true)}
                        onBlur={() => setShowBack(false)}
                        onChange={(e) =>
                            updateCard({
                                cvv: onlyNumbers(e.target.value).slice(0, 3),
                            })
                        }
                        error={card.cvv && !isValidCVV(card.cvv)}
                        fullWidth
                        sx={{
                            "& .MuiInputLabel-root": {
                                color: "#6b7280",
                                fontSize: 14,
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}

                    />
                </Stack>

                {/* 🔥 PARCELAMENTO */}
                {checkoutData.payment.method === "credit" && (
                    <TextField
                        select
                        label="Parcelamento"
                        value={installments}
                        onChange={(e) => updateInstallments(Number(e.target.value))}
                        fullWidth
                        sx={{
                            "& .MuiInputLabel-root": {
                                color: "#6b7280",
                                fontSize: 14,
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}

                    >
                        {installmentOptions.map((opt) => (
                          <MenuItem key={opt.installment} value={opt.installment}>
                            {opt.installment}x de R$ {opt.installmentValue.toFixed(2)}
                          </MenuItem>
                        ))}

                    </TextField>
                )}
                    
            </Stack>

            {/* AÇÕES */}
            <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={handleBack} fullWidth>
                    Voltar
                </Button>

                <Button
                    variant="contained"
                    disabled={!isValid}
                    onClick={handleConfirm}
                    fullWidth
                >
                    Revisar pedido
                </Button>
            </Stack>
        </Stack>
    );
}
